import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CamposCalculo } from '@/components/Costos/CamposCalculo';
import {
  armarCalculoPorcentual,
  BASES_CALCULO_VACIAS,
  baseEnMoneda,
  calcularMontoPorcentual,
  convertirImporte,
  ETIQUETA_BASE_CALCULO,
} from '@/components/Costos/calculo';
import type { AdicionalCalculo, BaseCalculo, BasesCalculo, Costo, ModoCalculo, TipoMoneda } from '@/types/costo';
import { formatearMonto } from '../utils/formatters';
import type { PresupuestoProformaItemFormData } from '../types';

type DatosCalculo = Pick<
  PresupuestoProformaItemFormData,
  'modo_calculo' | 'base_calculo' | 'porcentaje' | 'porcentaje_adicional' | 'suma_fija' | 'suma_fija_moneda'
>;

interface ModalCalculoItemProps {
  /** Ítem a editar; null cierra el modal. */
  item: Costo | null;
  /** Importe y moneda de la base imponible y del monto de venta. */
  bases?: BasesCalculo;
  /** Cotización del dólar: convierte la base si está en otra moneda que el ítem. */
  cotizacionDolar?: number | null;
  onClose: () => void;
  onGuardar: (itemId: string, data: DatosCalculo) => Promise<unknown>;
  isGuardando?: boolean;
}

const aTexto = (valor: number | null | undefined): string =>
  valor === null || valor === undefined ? '' : String(valor);

/**
 * Base con la que se abre el modal: la del ítem si ya es porcentual; si no, la
 * primera en la moneda del ítem.
 */
const baseInicial = (item: Costo | null, bases: BasesCalculo): BaseCalculo => {
  if (item?.modoCalculo === 'porcentaje' && item.baseCalculo) return item.baseCalculo;

  return bases.base_imponible.moneda !== item?.moneda && bases.monto_venta.moneda === item?.moneda
    ? 'monto_venta'
    : 'base_imponible';
};

/**
 * Edita cómo se calcula el monto de un ítem de presupuesto: fijo, o porcentaje
 * de la base imponible o del monto de venta más una suma fija o con otro
 * porcentaje aplicado al resultado.
 */
export const ModalCalculoItem: React.FC<ModalCalculoItemProps> = ({
  item,
  bases = BASES_CALCULO_VACIAS,
  cotizacionDolar = null,
  onClose,
  onGuardar,
  isGuardando = false,
}) => {
  const [modo, setModo] = useState<ModoCalculo>('fijo');
  const [baseCalculo, setBaseCalculo] = useState<BaseCalculo>('base_imponible');
  const [porcentaje, setPorcentaje] = useState('');
  const [adicional, setAdicional] = useState<AdicionalCalculo>('suma_fija');
  const [sumaFija, setSumaFija] = useState('');
  const [monedaSumaFija, setMonedaSumaFija] = useState<TipoMoneda>('ARS');
  const [porcentajeAdicional, setPorcentajeAdicional] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Abrirlo sobre un ítem fijo sugiere pasarlo a porcentaje.
    setModo(item ? 'porcentaje' : 'fijo');
    setBaseCalculo(baseInicial(item, bases));
    setPorcentaje(aTexto(item?.porcentaje));
    setAdicional(item?.porcentajeAdicional != null ? 'porcentaje' : 'suma_fija');
    setSumaFija(aTexto(item?.sumaFija));
    setMonedaSumaFija(item?.sumaFijaMoneda ?? item?.moneda ?? 'ARS');
    setPorcentajeAdicional(aTexto(item?.porcentajeAdicional));
    setError('');
  }, [item]);

  const { importe: importeBase, moneda: monedaBase } = bases[baseCalculo];
  const monedaItem = item?.moneda ?? 'ARS';
  const baseConvertida = baseEnMoneda(bases, baseCalculo, monedaItem, cotizacionDolar);
  const etiquetaBase = ETIQUETA_BASE_CALCULO[baseCalculo];

  const porcentajeNumerico = parseFloat(porcentaje);
  const sumaFijaNumerica = sumaFija.trim() === '' ? 0 : parseFloat(sumaFija);
  // Sin cotización, la suma fija en otra moneda no cuenta (igual que el backend).
  const sumaFijaConvertida = convertirImporte(sumaFijaNumerica, monedaSumaFija, monedaItem, cotizacionDolar) ?? 0;
  const porcentajeAdicionalNumerico = adicional === 'porcentaje' ? parseFloat(porcentajeAdicional) : null;
  const vistaPrevia =
    modo === 'porcentaje' &&
    !isNaN(porcentajeNumerico) &&
    !isNaN(sumaFijaNumerica) &&
    !Number.isNaN(porcentajeAdicionalNumerico)
      ? calcularMontoPorcentual(baseConvertida, porcentajeNumerico, sumaFijaConvertida, porcentajeAdicionalNumerico)
      : null;

  const handleGuardar = async () => {
    if (!item) return;

    if (modo === 'fijo') {
      await guardar({ modo_calculo: 'fijo' });
      return;
    }

    const calculo = armarCalculoPorcentual({ porcentaje, adicional, sumaFija, monedaSumaFija, porcentajeAdicional });
    if ('error' in calculo) {
      setError(calculo.error);
      return;
    }

    await guardar({ ...calculo.datos, base_calculo: baseCalculo });
  };

  const guardar = async (data: DatosCalculo) => {
    if (!item) return;

    try {
      await onGuardar(item.id, data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'No se pudo guardar el cálculo del ítem.');
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(abierto) => !abierto && !isGuardando && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cálculo de “{item?.nombre}”</DialogTitle>
          <DialogDescription>
            Un ítem calculado se actualiza solo cuando cambia la base imponible del presupuesto.
            Si después editás el monto a mano, pasa a ser un monto fijo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <CamposCalculo
            idPrefix="calculo-item"
            modo={modo}
            onModoChange={(valor) => {
              setModo(valor);
              setError('');
            }}
            baseCalculo={baseCalculo}
            onBaseCalculoChange={(valor) => {
              setBaseCalculo(valor);
              setError('');
            }}
            moneda={monedaItem}
            cotizacionDolar={cotizacionDolar}
            bases={bases}
            porcentaje={porcentaje}
            onPorcentajeChange={(valor) => {
              setPorcentaje(valor);
              setError('');
            }}
            adicional={adicional}
            onAdicionalChange={(valor) => {
              setAdicional(valor);
              setError('');
            }}
            sumaFija={sumaFija}
            onSumaFijaChange={(valor) => {
              setSumaFija(valor);
              setError('');
            }}
            monedaSumaFija={monedaSumaFija}
            onMonedaSumaFijaChange={(valor) => {
              setMonedaSumaFija(valor);
              setError('');
            }}
            porcentajeAdicional={porcentajeAdicional}
            onPorcentajeAdicionalChange={(valor) => {
              setPorcentajeAdicional(valor);
              setError('');
            }}
            disabled={isGuardando}
          />

          {modo === 'porcentaje' && (
            <p className="text-sm text-muted-foreground">
              {importeBase === null
                ? `El presupuesto todavía no tiene ${etiquetaBase}: por ahora solo cuenta la suma fija.`
                : `${etiquetaBase.charAt(0).toUpperCase()}${etiquetaBase.slice(1)}: ${formatearMonto(importeBase, monedaBase)}.`}
              {importeBase === null && adicional === 'porcentaje' && ' El monto queda en cero.'}
              {vistaPrevia !== null && (
                <>
                  {' '}Monto resultante:{' '}
                  <strong className="text-foreground">{formatearMonto(vistaPrevia, monedaItem)}</strong>
                </>
              )}
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGuardando}>
            Cancelar
          </Button>
          <Button onClick={() => void handleGuardar()} disabled={isGuardando}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
